import * as THREE from 'three'
import * as CANNON from 'cannon-es'
import Experience from '../Experience.js'
import SimpleCarModel from './SimpleCarModel.js'
import SimpleCarPhysics from './SimpleCarPhysics.js'

let instance = null

export default class SimpleCar {
    constructor() {
        // Singleton
        if (instance) {
            return instance
        }

        instance = this

        this.experience = new Experience()
        this.scene = this.experience.scene

        this.simpleCarModel = new SimpleCarModel()
        this.simpleCarPhysics = new SimpleCarPhysics()

        this.vehicle = this.simpleCarPhysics.vehicle

        this.time = this.experience.time

        this.frontWheelMesh1 = this.simpleCarModel.frontWheelMesh1
        this.frontWheelMesh2 = this.simpleCarModel.frontWheelMesh2
        this.rearWheelMesh1 = this.simpleCarModel.rearWheelMesh1
        this.rearWheelMesh2 = this.simpleCarModel.rearWheelMesh2
        this.chassisMesh = this.simpleCarModel.chassisMesh
        
        // For camera tracking
        this.forwardDirection = new THREE.Vector3(0, 0, 1)
        this.tempQuat = new THREE.Quaternion()
    }
    
    // Get the car's forward direction for camera
    getForwardDirection() {
        if (!this.chassisMesh) return this.forwardDirection
        
        // Get forward direction from chassis rotation
        this.tempQuat.copy(this.chassisMesh.quaternion)
        this.forwardDirection.set(0, 0, 1)
        this.forwardDirection.applyQuaternion(this.tempQuat)
        this.forwardDirection.y = 0
        this.forwardDirection.normalize()
        
        return this.forwardDirection
    }
    
    // Get car position
    getPosition() {
        if (!this.chassisMesh) return new THREE.Vector3(5, 0, 0)
        return this.chassisMesh.position.clone()
    }
    
    // Update car color from settings
    updateColor(colorHex) {
        if (this.chassisMesh && this.chassisMesh.children) {
            this.chassisMesh.children.forEach(child => {
                if (child.material && child.material.color) {
                    // Only update the main body, not windows or other parts
                    const currentHex = child.material.color.getHex()
                    // Check if it's a bright color (not black/gray for windows)
                    if (currentHex > 0x333333) {
                        child.material.color.set(colorHex)
                        if (child.material.emissive) {
                            child.material.emissive.set(colorHex)
                            child.material.emissiveIntensity = 0.1
                        }
                    }
                }
            })
        }
    }
    
    // Reset car position
    resetPosition() {
        const chassis = this.vehicle.chassisBody
        if (chassis) {
            // Stop all motion first
            chassis.velocity.setZero()
            chassis.angularVelocity.setZero()
            
            // Reset position - on the starting line
            chassis.position.set(55, 2, 5)
            
            // Reset rotation to face forward (along the track)
            chassis.quaternion.setFromAxisAngle(
                new CANNON.Vec3(0, 1, 0),
                -Math.PI / 2
            )
            
            // Wake up the body
            chassis.wakeUp()
        }
        
        // Reset wheels
        if (this.vehicle.wheelBodies) {
            this.vehicle.wheelBodies.forEach(wheel => {
                wheel.velocity.setZero()
                wheel.angularVelocity.setZero()
                wheel.wakeUp()
            })
        }
    }

    update() {
        if (this.chassisMesh) {
            this.simpleCarModel.updateMeshPosition(this.chassisMesh, this.vehicle.chassisBody)
        }

        if (this.frontWheelMesh1) {
            this.simpleCarModel.updateMeshPosition(this.frontWheelMesh1, this.vehicle.wheelBodies[2])
        }

        if (this.frontWheelMesh2) {
            this.simpleCarModel.updateMeshPosition(this.frontWheelMesh2, this.vehicle.wheelBodies[3])
        }

        if (this.rearWheelMesh1) {
            this.simpleCarModel.updateMeshPosition(this.rearWheelMesh1, this.vehicle.wheelBodies[0])
        }

        if (this.rearWheelMesh2) {
            this.simpleCarModel.updateMeshPosition(this.rearWheelMesh2, this.vehicle.wheelBodies[1])
        }
        
        // Update camera to follow car
        if (!this.experience.camera.testingMode && this.chassisMesh) {
            this.experience.camera.followTarget(
                this.chassisMesh.position,
                this.getForwardDirection()
            )
        }
    }
}